import { ReactNode, Fragment } from 'react';
import { Box, Flex, Stack } from '@chakra-ui/react';
import { overflowScrollbarStyles } from '@duke-hq/ui/styles/overflowScrollbarStyles';

type TableViewLayoutPropTypes = {
  pageHeader?: ReactNode | ReactNode[];
  pageActions?: ReactNode | ReactNode[];
  filters?: ReactNode | ReactNode[];
  table: ReactNode;
  pagination?: ReactNode;
  tableActionsRight?: ReactNode | ReactNode[];
  tableActionsLeft?: ReactNode | ReactNode[];
  tableHeight?: string | number;
  tableBgColor?: string;
};

/**
 * TableViewLayout is a reusable layout component that includes a header row, filters row, table, and pagination.
 * @props The properties that define the content in different parts of the TableViewLayout component.
 * @returns The TableViewLayout component.
 * @see {@link README.md} for more information.
 */
export default function TableViewLayout({
  pageHeader,
  pageActions,
  filters,
  table,
  pagination,
  tableActionsRight,
  tableActionsLeft,
  tableHeight = 'full',
  tableBgColor = 'bg.surface',
}: TableViewLayoutPropTypes) {
  return (
    <Box
      bg={tableBgColor}
      width="full"
      overflow="auto"
      height={tableHeight}
      css={overflowScrollbarStyles}
    >
      <Stack bg={tableBgColor} gap="0" h="full" w="full">
        {/* Header Row */}
        <Flex justify="space-between" alignItems="center" gap="2" p="4">
          {/* Header Left Side Components */}
          <Flex justify="start" alignItems="center" gap="2">
            {Array.isArray(pageHeader)
              ? pageHeader.map((component, index) => <Fragment key={index}>{component}</Fragment>)
              : pageHeader}
          </Flex>
          {/* Header Right Side Components */}
          <Flex justify="end" alignItems="center" gap="2">
            {Array.isArray(pageActions)
              ? pageActions.map((action, index) => <Fragment key={index}>{action}</Fragment>)
              : pageActions}
          </Flex>
        </Flex>

        {/* Filters Row */}
        {filters ? (
          <Flex width="full" justify="start" alignItems="center" gap="2" px="4" pb="4">
            {/* Filters Components */}
            <Flex width="full" justify="start" alignItems="center" gap="2">
              {Array.isArray(filters)
                ? filters.map((filter, index) => <Fragment key={index}>{filter}</Fragment>)
                : filters}
            </Flex>
          </Flex>
        ) : null}

        {/* Table Actions Row */}
        {tableActionsRight || tableActionsLeft ? (
          <Flex justify="space-between" alignItems="center" gap="2" px="4" pb="3">
            {/* Table Actions Left Side Components */}
            <Flex justify="start" alignItems="center" gap="2">
              {Array.isArray(tableActionsLeft)
                ? tableActionsLeft.map((component, index) => (
                    <Fragment key={index}>{component}</Fragment>
                  ))
                : tableActionsLeft}
            </Flex>
            {/* Table Actions Right Side Components */}
            <Flex justify="end" alignItems="center" gap="2">
              {Array.isArray(tableActionsRight)
                ? tableActionsRight.map((component, index) => (
                    <Fragment key={index}>{component}</Fragment>
                  ))
                : tableActionsRight}
            </Flex>
          </Flex>
        ) : null}

        {/* Table Container */}
        <Box h={tableHeight} overflowX="auto" css={overflowScrollbarStyles}>
          {table}
        </Box>

        {/* Pagination */}
        {pagination ? (
          <Box py="3" px="4">
            {pagination}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
